/**
 * Telemetry — error reporting + metrics + boot probes.
 *
 * The plan calls for a wire to Sentry / Datadog / an in-house
 * collector. Today we POST events to `${apiBaseUrl}/client-errors`,
 * which the api-server (or a sidecar) can forward to whatever
 * collector the tenant uses. The payload is intentionally small
 * and PII-stripped: only `user.id`, no email, no display name, no
 * tokens.
 *
 * In dev/local we log to `console.error`/`console.info` and skip
 * the network call so the dev console stays uncluttered.
 *
 * Module layout:
 *   - `reportError(error, context)` — fire-and-forget.
 *   - `reportMetric(name, value, tags)` — counters / timings.
 *   - `BootProbe` — React component that fires one event on the
 *      first successful render so dashboards can detect "the SPA
 *      booted" without external pings.
 *   - `installBootProbes()` — called from main.tsx after the
 *      runtime config has loaded; emits one event per stage.
 */

import { getOptionalRuntimeConfig } from '../config/runtime-config';

declare const __APP_VERSION__: string;

const ENDPOINT = '/client-errors';

interface ErrorContext {
  /** Free-form component / feature tag (e.g. 'dispatch-form'). */
  feature?: string;
  /** Operation that triggered the error (e.g. 'submit', 'load'). */
  op?: string;
  /** Arbitrary key/value pairs. Will be JSON-serialised. */
  tags?: Record<string, string | number | boolean | null | undefined>;
}

interface ErrorPayload {
  kind: 'error';
  message: string;
  stack?: string;
  url: string;
  userAgent: string;
  version: string;
  env: string;
  ts: string;
  context: ErrorContext;
}

interface MetricPayload {
  kind: 'metric';
  name: string;
  value: number;
  tags?: Record<string, string | number | boolean | null | undefined>;
  url: string;
  version: string;
  env: string;
  ts: string;
}

type TelemetryPayload = ErrorPayload | MetricPayload;

/** In-memory buffer for the dev console + tests. */
const recentEvents: TelemetryPayload[] = [];
const MAX_BUFFER = 50;

function bufferEvent(ev: TelemetryPayload): void {
  recentEvents.push(ev);
  if (recentEvents.length > MAX_BUFFER) recentEvents.shift();
}

/** Test helper: snapshot the buffered events. */
export function getRecentTelemetry(): readonly TelemetryPayload[] {
  return recentEvents;
}

/** Test helper: clear the buffer. */
export function __clearTelemetryForTests(): void {
  recentEvents.length = 0;
}

/**
 * The active site, set by `SiteProvider` whenever it changes.
 * Threaded into every `reportError` call's tags automatically (rather
 * than requiring every call site to remember to pass it) so the
 * backend's `/client-errors` sink (Workstream A2) can resolve which
 * site an error belongs to — without it, an error would land in the
 * `issues` table with `site_id: null` and never surface in any
 * site-scoped Issues view. Module-level, not React context, since
 * this needs to be readable from non-component code (`main.tsx`'s
 * top-level error listeners run before any provider mounts).
 */
let _currentSiteId: string | null = null;

export function setTelemetrySite(siteId: string | null): void {
  _currentSiteId = siteId;
}

/** The active site as last published by `SiteProvider`, for non-React
 *  code that must name it (the role-preview audit write). */
export function getTelemetrySite(): string | null {
  return _currentSiteId;
}

function isProductionLike(): boolean {
  const cfg = getOptionalRuntimeConfig();
  return cfg !== null && cfg.env !== 'local';
}

function basePayload(): Pick<ErrorPayload, 'url' | 'userAgent' | 'version' | 'env' | 'ts'> {
  return {
    url: typeof window !== 'undefined' ? window.location.href : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev',
    env: getOptionalRuntimeConfig()?.env ?? 'unknown',
    ts: new Date().toISOString(),
  };
}

function postOrLog(payload: TelemetryPayload): void {
  bufferEvent(payload);
  // Always log to console in dev for local debugging.
  if (payload.kind === 'error') {
     
    console.error('[telemetry:error]', payload);
  } else {
     
    console.info('[telemetry:metric]', payload);
  }
  // In local dev we skip the network call.
  if (!isProductionLike()) return;
  // In production, fire-and-forget POST. We don't await because
  // the call site shouldn't block on telemetry.
  if (typeof fetch === 'undefined') return;
  const cfg = getOptionalRuntimeConfig();
  if (!cfg) return;
  void fetch(`${cfg.apiBaseUrl}${ENDPOINT}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch((err) => {
     
    console.warn('[telemetry] POST failed', err);
  });
}

/** Report a render-time or runtime error to the telemetry backend. */
export function reportError(error: unknown, context: ErrorContext = {}): void {
  const err = error instanceof Error ? error : new Error(String(error));
  const withSite: ErrorContext = _currentSiteId
    ? { ...context, tags: { ...context.tags, site: _currentSiteId } }
    : context;
  const payload: ErrorPayload = {
    kind: 'error',
    message: err.message,
    stack: err.stack,
    ...basePayload(),
    context: stripPiiContext(withSite),
  };
  postOrLog(payload);
}

/** Report a counter or timing metric. */
export function reportMetric(
  name: string,
  value: number,
  tags: Record<string, string | number | boolean | null | undefined> = {},
): void {
  const payload: MetricPayload = {
    kind: 'metric',
    name,
    value,
    tags: stripPiiTags(tags),
    ...basePayload(),
  };
  postOrLog(payload);
}

/**
 * Strip anything that smells like PII from a context object.
 *
 * The whitelist is generous — most categorical keys are kept. We
 * block only keys whose names look like PII carriers (email,
 * name, token, etc.). The api-server re-validates and the PII
 * filter is defence-in-depth.
 */
const PII_KEY_PATTERNS: RegExp[] = [
  /email/i,
  /password/i,
  /secret/i,
  /token/i,
  /\bname\b/i,
  /displayname/i,
  /phone/i,
  /address/i,
  /ssn/i,
];

function isPiiKey(k: string): boolean {
  return PII_KEY_PATTERNS.some((rx) => rx.test(k));
}

function stripPiiContext(ctx: ErrorContext): ErrorContext {
  const out: ErrorContext = {};
  if (ctx.feature !== undefined) out.feature = String(ctx.feature).slice(0, 64);
  if (ctx.op !== undefined) out.op = String(ctx.op).slice(0, 64);
  if (ctx.tags) {
    const tags: Record<string, string | number | boolean | null | undefined> = {};
    for (const [k, v] of Object.entries(ctx.tags)) {
      if (isPiiKey(k)) continue;
      tags[k] = v;
    }
    out.tags = tags;
  }
  return out;
}

function stripPiiTags(
  tags: Record<string, string | number | boolean | null | undefined>,
): Record<string, string | number | boolean | null | undefined> {
  const out: Record<string, string | number | boolean | null | undefined> = {};
  for (const [k, v] of Object.entries(tags)) {
    if (isPiiKey(k)) continue;
    out[k] = v;
  }
  return out;
}

/**
 * Emit one event per boot stage. Called from `main.tsx` after the
 * runtime config has loaded. Each call records the wall-clock
 * duration since the previous probe so dashboards can compute
 * boot latency.
 */
const bootStart = typeof performance !== 'undefined' ? performance.now() : Date.now();
let lastProbe = bootStart;

export function bootProbe(stage: string, extra: Record<string, string | number> = {}): void {
  const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const deltaMs = Math.round(now - lastProbe);
  const totalMs = Math.round(now - bootStart);
  lastProbe = now;
  reportMetric(`boot.${stage}`, totalMs, { deltaMs, ...extra });
}

/**
 * Convenience: emit a counter without explicit value (defaults to 1).
 */
export function countEvent(name: string, tags: Record<string, string | number | boolean | null | undefined> = {}): void {
  reportMetric(name, 1, tags);
}
